import { useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERYKEY_CHECK_IMPERSONATION } from "./queryKeys";
import {
  isImpersonated,
  logoutImpersonation,
} from "src/services/impersonation";
import { minutesToMilliseconds } from "../helpers";
import { useDispatch } from "react-redux";
import {
  fetchApplicationAndDependencies,
  RESET_STORE,
  setImpersonator,
  setImpersonatorTimedOut,
  setImpersonatorTimingOut,
} from "src/redux/actions";
import { IMPERSONATION_ID, IMPERSONATION_TOKEN } from "../constants";
import { decrementUserSession } from "src/services/logout";
import { removeScoutFromPage } from "src/services/scout";

const CHECK_IMPERSONATION_INTERVAL = minutesToMilliseconds(4);
const IMPERSONATION_TIMEOUT = minutesToMilliseconds(14);
const IMPERSONATION_FINAL_TIMEOUT = minutesToMilliseconds(1);

export function useCheckImpersonation(
  isImpersonating: string,
  loading: boolean,
) {
  return useQuery({
    queryKey: [QUERYKEY_CHECK_IMPERSONATION],
    refetchInterval: CHECK_IMPERSONATION_INTERVAL,
    queryFn: isImpersonated,
    enabled: Boolean(!isImpersonating && !loading),
  });
}

export function useImpersonation() {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const impersonationTimeout = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const impersonationFinalTimeout = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  function navToDashboard() {
    if (location.pathname !== "/") {
      history.push("/");
    }
  }

  const initializeImpersonation = ({
    email,
    id,
    token,
  }: {
    email: string;
    id: string;
    token: string;
  }) => {
    queryClient.invalidateQueries();
    dispatch({ type: RESET_STORE });

    removeScoutFromPage();
    // Make sure we are on the dashboard when switching
    navToDashboard();
    // Set Impersonator Redux State
    dispatch(setImpersonator(email));
    // Setup timer to begin logout process in 14 minutes
    impersonationTimeout.current = setTimeout(
      impersonationTimeoutHandler,
      IMPERSONATION_TIMEOUT,
    );
    // Add event listeners to reset logout timers, clear impersonation session on app close
    addImpersonationEventListeners();
    // Add our impersonation data to session storage
    sessionStorage.setItem(IMPERSONATION_ID, id);
    sessionStorage.setItem(IMPERSONATION_TOKEN, token);
    // Restart the application which will fetch with our newly added session storage token
    dispatch(fetchApplicationAndDependencies());
  };

  const clearImpersonation = ({
    showTimedOutMessage = false,
  }: { showTimedOutMessage?: boolean } = {}) => {
    queryClient.invalidateQueries();
    dispatch({ type: RESET_STORE });
    // Decrement impersonated user session
    decrementUserSession();
    removeScoutFromPage();
    // Make sure we are on the dashboard when switching
    navToDashboard();
    // Clear redux impersonator state
    dispatch(setImpersonator());
    // Clear our B/E impersonation session
    logoutImpersonation();
    // Clear event listeners, timers, session storage
    removeImpersonationEventListeners();
    clearTimeout(impersonationTimeout.current);
    clearTimeout(impersonationFinalTimeout.current);
    sessionStorage.removeItem(IMPERSONATION_ID);
    sessionStorage.removeItem(IMPERSONATION_TOKEN);
    // Restart application which will fetch everything based on the logged in user's jwt
    dispatch(fetchApplicationAndDependencies());
    if (showTimedOutMessage) {
      dispatch(setImpersonatorTimingOut(false));
      dispatch(setImpersonatorTimedOut(true));
    }
  };

  const impersonationTimeoutHandler = () => {
    dispatch(setImpersonatorTimingOut(true));
    impersonationFinalTimeout.current = setTimeout(
      impersonationFinalTimeoutHandler,
      IMPERSONATION_FINAL_TIMEOUT,
    );
  };

  const impersonationFinalTimeoutHandler = () => {
    clearImpersonation({ showTimedOutMessage: true });
  };

  const addImpersonationEventListeners = () => {
    window.addEventListener("click", resetTimers);
    window.addEventListener("visibilitychange", handleVisibilityChange);
  };

  const removeImpersonationEventListeners = () => {
    window.removeEventListener("click", resetTimers);
    window.removeEventListener("visibilitychange", handleVisibilityChange);
  };

  const resetTimers = useCallback(() => {
    clearTimeout(impersonationTimeout.current);
    clearTimeout(impersonationFinalTimeout.current);
    impersonationTimeout.current = setTimeout(
      impersonationTimeoutHandler,
      IMPERSONATION_TIMEOUT,
    );
  }, []);

  const continueImpersonationSession = () => {
    resetTimers();
    dispatch(setImpersonatorTimingOut(false));
  };

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState !== "visible") {
      impersonationFinalTimeoutHandler();
    }
  }, []);

  return {
    initializeImpersonation,
    clearImpersonation,
    continueImpersonationSession,
  };
}
